import {
  AdminPermissionKey,
  getFirstPermittedAdminPath,
  hasAdminPermission,
} from "../Util/adminPermissions";
import PackageSubscriptionReports from "../Pages/Admin/PackageSubscriptionReports/PackageSubscriptionReports";
import ProfitQuarterReport from "../Pages/Admin/ProfitQuarterReport/ProfitQuarterReport";
import AmenityUsageReport from "../Pages/Admin/AmenityUsageReport/AmenityUsageReport";
import EditNotification from "../Pages/Admin/EditNotification/EditNotification";
import UpdateAssetType from "../Pages/Admin/UpdateAssetType/UpdateAssetType";
import ResidentReports from "../Pages/Admin/ResidentReports/ResidentReports";
import AddNotification from "../Pages/Admin/AddNotification/AddNotification";
import PrivateRoute from "../Components/Common/PrivateRoute/PrivateRoute";
import FutureBookings from "../Pages/Admin/FutureBookings/FutureBookings";
import UnauthorizedPage from "../Pages/UnauthorizedPage/UnauthorizedPage";
import MobileHeader from "../Components/Admin/MobileHeader/MobileHeader";
import PublicRoute from "../Components/Common/PublicRoute/PublicRoute";
import Notifications from "../Pages/Admin/Notifications/Notifications";
import EditNonMember from "../Pages/Admin/EditNonMember/EditNonMember";
import CouponsReport from "../Pages/Admin/CouponsReport/CouponsReport";
import AddFreeMember from "../Pages/Admin/AddFreeMember/AddFreeMember";
import FreeMembers from "../Components/Admin/FreeMembers/FreeMembers";
import ProfitReport from "../Pages/Admin/ProfitReport/ProfitReport";
import PastBookings from "../Pages/Admin/PastBookings/PastBookings";
import MemberReport from "../Pages/Admin/MemberReport/MemberReport";
import MemberDetail from "../Pages/Admin/MemberDetail/MemberDetail";
import EditBuilding from "../Pages/Admin/EditBuilding/EditBuilding";
import AddAssetType from "../Pages/Admin/AddAssetType/AddAssetType";
import DoorHistory from "../Pages/Admin/DoorHistory/DoorHistory";
import AddBuilding from "../Pages/Admin/AddBuilding/AddBuilding";
import NonMembers from "../Pages/Admin/NonMembers/NonMembers";
import EditMember from "../Pages/Admin/EditMember/EditMember";
import EditCoupon from "../Pages/Admin/EditCoupon/EditCoupon";
import { Navigate, Outlet, RouteObject } from "react-router";
import AddMember from "../Pages/Admin/AddMembers/AddMember";
import EditGroup from "../Pages/Admin/EditGroup/EditGroup";
import EditAsset from "../Pages/Admin/EditAsset/EditAsset";
import Dashboard from "../Pages/Admin/Dashboard/Dashboard";
import Buildings from "../Pages/Admin/Buildings/Buildings";
import AddCoupon from "../Pages/Admin/AddCoupon/AddCoupon";
import Sidebar from "../Components/Admin/Sidebar/Sidebar";
import EditUser from "../Pages/Admin/EditUser/EditUser";
import EditRole from "../Pages/Admin/EditRole/EditRole";
import AddGroup from "../Pages/Admin/AddGroup/AddGroup";
import AddAsset from "../Pages/Admin/AddAsset/AddAsset";
import Header from "../Components/Admin/Header/Header";
import Tickets from "../Pages/Admin/Tickets/Tickets";
import Members from "../Pages/Admin/Members/Members";
import Coupons from "../Pages/Admin/Coupons/Coupons";
import AddUser from "../Pages/Admin/AddUser/AddUser";
import AddRole from "../Pages/Admin/AddRole/AddRole";
import Groups from "../Pages/Admin/Groups/Groups";
import Assets from "../Pages/Admin/Assets/Assets";
import Users from "../Pages/Admin/Users/Users";
import Roles from "../Pages/Admin/Roles/Roles";
import Login from "../Pages/Admin/Login/Login";
import styles from "./AdminRoutes.module.scss";
import Violations from "../Pages/Admin/Violations/Violations";

export type { AdminPermissionKey };
export { getFirstPermittedAdminPath, hasAdminPermission };

type AdminRouteHandle = {
  permission?: AdminPermissionKey;
};

type AdminRouteObject = RouteObject & {
  handle?: AdminRouteHandle;
  children?: AdminRouteObject[];
};

const AdminLayout = () => {
  return (
    <div className={styles.userLayoutContainer}>
      <div className={styles.userLayoutHeader}>
        <div className={styles.desktopHeader}>
          <Header />
        </div>
        <div className={styles.mobileHeader}>
          <MobileHeader />
        </div>
      </div>
      <div className={styles.userLayoutBody}>
        <div className={styles.sidebar}>
          <Sidebar />
        </div>
        <div className={styles.userLayoutContent}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

const adminChildren: AdminRouteObject[] = [
  // Dashboard
  {
    path: "/admin",
    handle: { permission: "dashboard_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Dashboard />
      </PrivateRoute>
    ),
  },

  // Buildings
  {
    path: "/admin/buildings",
    handle: { permission: "buildings_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Buildings />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/buildings/add",
    handle: { permission: "buildings_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddBuilding />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/buildings/edit/:id",
    handle: { permission: "buildings_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditBuilding />
      </PrivateRoute>
    ),
  },

  // Groups
  {
    path: "/admin/groups",
    handle: { permission: "groups_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Groups />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/groups/add",
    handle: { permission: "groups_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddGroup />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/groups/edit/:id",
    handle: { permission: "groups_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditGroup />
      </PrivateRoute>
    ),
  },

  // Assets
  {
    path: "/admin/assets",
    handle: { permission: "assets_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Assets />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/assets/add",
    handle: { permission: "assets_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddAsset />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/assets/edit/:id",
    handle: { permission: "assets_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditAsset />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/assets/assettype/add",
    handle: { permission: "assets_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddAssetType />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/assets/assettype/edit/:id",
    handle: { permission: "assets_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <UpdateAssetType />
      </PrivateRoute>
    ),
  },

  // Roles
  {
    path: "/admin/roles",
    handle: { permission: "roles_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Roles />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/roles/add",
    handle: { permission: "roles_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddRole />
      </PrivateRoute>
    ),
  },

  {
    path: "/admin/roles/edit/:id",
    handle: { permission: "roles_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditRole />
      </PrivateRoute>
    ),
  },

  // Users
  {
    path: "/admin/users",
    handle: { permission: "users_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Users />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/users/add",
    handle: { permission: "users_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddUser />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/users/edit/:id",
    handle: { permission: "users_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditUser />
      </PrivateRoute>
    ),
  },

  // Members
  {
    path: "/admin/members",
    handle: { permission: "members_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Members />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/members/add",
    handle: { permission: "members_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddMember />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/members/detail/:id",
    handle: { permission: "members_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <MemberDetail />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/members/edit/:id",
    handle: { permission: "members_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditMember />
      </PrivateRoute>
    ),
  },

  // Non Members
  {
    path: "/admin/nonmembers",
    handle: { permission: "members_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <NonMembers />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/nonmembers/detail/:id",
    handle: { permission: "members_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <MemberDetail />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/nonmembers/edit/:id",
    handle: { permission: "members_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditNonMember />
      </PrivateRoute>
    ),
  },

  // Free Members
  {
    path: "/admin/freemembers",
    handle: { permission: "members_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <FreeMembers />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/freemembers/add",
    handle: { permission: "members_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddFreeMember />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/freemembers/detail/:id",
    handle: { permission: "members_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <MemberDetail />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/freemembers/edit/:id",
    handle: { permission: "members_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditNonMember />
      </PrivateRoute>
    ),
  },

  // Coupons (vouchers)
  {
    path: "/admin/coupons",
    handle: { permission: "vouchers_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Coupons />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/coupons/add",
    handle: { permission: "vouchers_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddCoupon />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/coupons/edit/:id",
    handle: { permission: "vouchers_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditCoupon />
      </PrivateRoute>
    ),
  },

  // Tickets

  {
    path: "/admin/tickets",
    handle: { permission: "tickets_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Tickets />
      </PrivateRoute>
    ),
  },
  // Notifications

  {
    path: "/admin/notifications",
    handle: { permission: "notification_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Notifications />
      </PrivateRoute>
    ),
  },

  {
    path: "/admin/notifications/add",
    handle: { permission: "notification_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AddNotification />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/notifications/edit/:id",
    handle: { permission: "notification_manage" },
    element: (
      <PrivateRoute isAdmin={true}>
        <EditNotification />
      </PrivateRoute>
    ),
  },
  // Reports
  {
    path: "/admin/reports/amenityusage",
    handle: { permission: "reportsamenityusage_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <AmenityUsageReport />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/members",
    handle: { permission: "reportsbuildingmember_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <MemberReport />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/residents",
    handle: { permission: "reportsresident_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <ResidentReports />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/package",
    handle: { permission: "reportspackagesubscription_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <PackageSubscriptionReports />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/futurebookings",
    handle: { permission: "reportsbooking_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <FutureBookings />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/coupons",
    handle: { permission: "reportsvoucher_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <CouponsReport />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/profit",
    handle: { permission: "reportsownerprofit_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <ProfitReport />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/profit-quarter",
    handle: { permission: "reportsquarterprofit_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <ProfitQuarterReport />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/pastbookings",
    handle: { permission: "reportsbooking_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <PastBookings />
      </PrivateRoute>
    ),
  },
  {
    path: "/admin/reports/doorhistory",
    handle: { permission: "buildings_doorlog" },
    element: (
      <PrivateRoute isAdmin={true}>
        <DoorHistory />
      </PrivateRoute>
    ),
  },

  // Violations
  {
    path: "/admin/violations",
    handle: { permission: "memberviolations_view" },
    element: (
      <PrivateRoute isAdmin={true}>
        <Violations />
      </PrivateRoute>
    ),
  },
];

export const getAdminRoutes = (
  permissions: Record<string, boolean> | null | undefined,
): RouteObject[] => {
  const canViewDashboard = hasAdminPermission(permissions, "dashboard_view");
  const firstPermittedPath = getFirstPermittedAdminPath(permissions);

  const filteredChildren = adminChildren.filter((route) => {
    // `/admin` is handled below so we can redirect when dashboard is denied
    if (route.path === "/admin") {
      return false;
    }
    return hasAdminPermission(permissions, route.handle?.permission);
  });

  const homeRoute: AdminRouteObject = canViewDashboard
    ? {
        path: "/admin",
        handle: { permission: "dashboard_view" },
        element: (
          <PrivateRoute isAdmin={true}>
            <Dashboard />
          </PrivateRoute>
        ),
      }
    : {
        path: "/admin",
        element: (
          <PrivateRoute isAdmin={true}>
            {firstPermittedPath !== "/admin" ? (
              <Navigate to={firstPermittedPath} replace />
            ) : (
              <UnauthorizedPage />
            )}
          </PrivateRoute>
        ),
      };

  return [
    {
      path: "/admin/login",
      element: (
        <PublicRoute isAdmin={true} isAuthPage={true}>
          <Login />
        </PublicRoute>
      ),
    },
    {
      element: <AdminLayout />,
      children: [homeRoute, ...filteredChildren],
    },
  ];
};

export default getAdminRoutes;
